/**
 * Admin Advertising - Interactive Banner Editor with Full Styling Controls
 * Features: Image upload, positioning, styling, per-placement visibility, max carousel ads, stats & export
 */
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { 
  Megaphone, Plus, Edit2, Trash2, Eye, EyeOff, Loader2, 
  Search, X, BarChart2, MousePointer, Image, Upload,
  Monitor, Smartphone, Check, Building2, RefreshCw,
  Type, Square, Maximize2, GripVertical, Palette, Sun, Layers,
  Grid3X3, LayoutGrid, Download, Calendar, TrendingUp, Activity, AlertCircle, Tag
} from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';

// Helper to detect if URL is a video file
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

// Media Preview component - renders image or video
const MediaPreview = memo(({ src, alt, className, onError }) => {
  if (!src) return null;
  
  if (isVideoUrl(src)) {
    return (
      <video
        src={src}
        className={`${className} pointer-events-none`}
        autoPlay
        loop
        muted
        playsInline
        onError={onError}
      />
    );
  }
  
  return (
    <img
      src={src}
      alt={alt || 'Preview'}
      className={className}
      onError={onError}
    />
  );
});

// Placement configurations - Homepage + Category banner locations + Listing Grid
// SYNCED with PromoBlock.js aspect ratios: Large = 6:1 desktop / 2:1 mobile, Double = 16:9 both
const PLACEMENTS = {
  home_top: {
    id: 'home_top',
    name: 'Homepage Top',
    description: 'Large carousel below "Just Listed" section',
    desktop: { width: 1200, height: 200, label: '1200×200 (6:1)' },
    mobile: { width: 800, height: 400, label: '800×400 (2:1)' },
    color: '#3B82F6',
    type: 'carousel',
    icon: 'layers'
  },
  home_middle: {
    id: 'home_middle', 
    name: 'Homepage Middle (2-Up)',
    description: 'Two banners side by side below "Featured Listings"',
    desktop: { width: 800, height: 400, label: '800×400 (2:1)' },
    mobile: { width: 800, height: 400, label: '800×400 (2:1)' },
    color: '#F59E0B',
    type: 'double', // Special type for 2-up layout
    hasLeftRight: true, // Indicates left/right position option
    icon: 'layers'
  },
  home_bottom: {
    id: 'home_bottom',
    name: 'Homepage Bottom (2-Up)',
    description: 'Two banners side by side above footer CTA',
    desktop: { width: 800, height: 400, label: '800×400 (2:1)' },
    mobile: { width: 800, height: 400, label: '800×400 (2:1)' },
    color: '#8B5CF6',
    type: 'double', // 2-up layout like home_middle
    hasLeftRight: true, // Indicates left/right position option
    icon: 'layers'
  },
  category_landing: {
    id: 'category_landing',
    name: 'Category Landing',
    description: 'Hero banner on category landing pages',
    desktop: { width: 1200, height: 200, label: '1200×200 (6:1)' },
    mobile: { width: 800, height: 400, label: '800×400 (2:1)' },
    color: '#10B981',
    type: 'single',
    hasCategoryTargeting: true, // Enable category filter
    icon: 'layers'
  },
  listing_grid: {
    id: 'listing_grid',
    name: 'In-Grid Ads',
    description: 'Ads inserted between marketplace listings',
    desktop: { width: 320, height: 400, label: '320×400 (4:5)' },
    mobile: { width: 200, height: 250, label: '200×250 (4:5)' },
    color: '#EC4899',
    type: 'grid', // Special type for frequency-based placement
    hasCategoryTargeting: true,
    icon: 'grid'
  },
  listing_detail: {
    id: 'listing_detail',
    name: 'Listing Detail Page',
    description: 'Banner above safety tips on listing pages - filtered by listing category',
    desktop: { width: 1200, height: 200, label: '1200×200 (6:1)' },
    mobile: { width: 800, height: 400, label: '800×400 (2:1)' },
    color: '#06B6D4',
    type: 'carousel',
    hasCategoryTargeting: true,
    icon: 'layers'
  }
};

// Position options
const POSITION_OPTIONS = [
  { id: 'top-left', label: 'Top Left' },
  { id: 'top-center', label: 'Top Center' },
  { id: 'top-right', label: 'Top Right' },
  { id: 'center-left', label: 'Center Left' },
  { id: 'center', label: 'Center' },
  { id: 'center-right', label: 'Center Right' },
  { id: 'bottom-left', label: 'Bottom Left' },
  { id: 'bottom-center', label: 'Bottom Center' },
  { id: 'bottom-right', label: 'Bottom Right' },
];

// Text size options
const TEXT_SIZES = [
  { id: 'small', label: 'S', title: 'Small' },
  { id: 'medium', label: 'M', title: 'Medium' },
  { id: 'large', label: 'L', title: 'Large' },
];

// Button style options
const BUTTON_STYLES = [
  { id: 'filled', label: 'Filled' },
  { id: 'outline', label: 'Outline' },
];

// Preset button colors
const BUTTON_COLORS = [
  { id: '#3B82F6', label: 'Blue' },
  { id: '#10B981', label: 'Green' },
  { id: '#F59E0B', label: 'Amber' },
  { id: '#EF4444', label: 'Red' },
  { id: '#8B5CF6', label: 'Purple' },
  { id: '#EC4899', label: 'Pink' },
  { id: '#FFFFFF', label: 'White' },
  { id: '#000000', label: 'Black' },
];

// Get CSS positioning for an element
const getPositionStyles = (position) => {
  const styles = { position: 'absolute' };
  if (position.includes('top')) styles.top = '12px';
  else if (position.includes('bottom')) styles.bottom = '12px';
  else styles.top = '50%';
  if (position.includes('left')) { styles.left = '12px'; styles.textAlign = 'left'; }
  else if (position.includes('right')) { styles.right = '12px'; styles.textAlign = 'right'; }
  else { styles.left = '50%'; styles.textAlign = 'center'; }
  if (position === 'center') styles.transform = 'translate(-50%, -50%)';
  else if (position.includes('center') && !position.includes('left') && !position.includes('right')) styles.transform = 'translateX(-50%)';
  else if (!position.includes('top') && !position.includes('bottom')) styles.transform = 'translateY(-50%)';
  return styles;
};

// Get text size in pixels
const getTextSize = (size, viewMode, isTitle = true) => {
  const sizes = {
    small: { desktop: isTitle ? 16 : 11, mobile: isTitle ? 12 : 9 },
    medium: { desktop: isTitle ? 22 : 13, mobile: isTitle ? 14 : 10 },
    large: { desktop: isTitle ? 28 : 15, mobile: isTitle ? 18 : 11 },
  };
  return sizes[size]?.[viewMode] || sizes.medium[viewMode];
};

// Position Selector Grid
const PositionSelector = memo(({ value, onChange, label, color }) => (
  <div className="space-y-1">
    <label className="block text-xs font-medium text-muted-foreground">{label}</label>
    <div className="grid grid-cols-3 gap-0.5 p-1 bg-secondary/50 rounded-lg w-fit">
      {POSITION_OPTIONS.map(pos => (
        <button key={pos.id} type="button" onClick={() => onChange(pos.id)}
          className={`w-5 h-5 rounded flex items-center justify-center transition-all ${value === pos.id ? 'ring-1 ring-offset-1 ring-offset-background' : 'hover:bg-secondary'}`}
          style={value === pos.id ? { backgroundColor: color, color: 'white' } : {}}
          title={pos.label}>
          <div className={`w-1 h-1 rounded-sm ${value === pos.id ? 'bg-white' : 'bg-muted-foreground/30'}`} />
        </button>
      ))}
    </div>
  </div>
));

// Image Upload Component with Dimension Detection
const ImageUploader = memo(({ value, onChange, recommendedSize, label }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [imageDimensions, setImageDimensions] = useState(null);
  const fileInputRef = useRef(null);

  // Check if the value is a video
  const isVideo = isVideoUrl(value);

  // Check image dimensions when value changes (only for images)
  useEffect(() => {
    if (value && !isVideoUrl(value)) {
      const img = new window.Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => setImageDimensions(null);
      img.src = value;
    } else {
      setImageDimensions(null);
    }
  }, [value]);

  // Check if dimensions match recommendation
  const getDimensionStatus = () => {
    if (!imageDimensions || !recommendedSize) return null;
    const { width, height } = imageDimensions;
    const { width: recWidth, height: recHeight } = recommendedSize;
    
    // Allow 10% tolerance
    const widthMatch = Math.abs(width - recWidth) / recWidth <= 0.1;
    const heightMatch = Math.abs(height - recHeight) / recHeight <= 0.1;
    const aspectRatioMatch = Math.abs((width / height) - (recWidth / recHeight)) < 0.1;
    
    if (widthMatch && heightMatch) return 'perfect';
    if (aspectRatioMatch) return 'good';
    return 'warning';
  };

  const dimensionStatus = getDimensionStatus();

  const handleUpload = async (file) => {
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideoFile = file.type.startsWith('video/');
    if (!isImage && !isVideoFile) { toast.error('Please select an image or video file'); return; }
    // Different size limits: 10MB for images, 15MB for videos
    const maxSize = isVideoFile ? 15 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) { 
      toast.error(isVideoFile ? 'Video must be less than 15MB' : 'Image must be less than 10MB'); 
      return; 
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      // Use /upload/banner endpoint for admin banner uploads (supports images and videos)
      const response = await axios.post(`${API}/upload/banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) { 
        onChange(response.data.url); 
        toast.success(isVideoFile ? 'Video uploaded!' : 'Image uploaded!'); 
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium">{label}</label>}
      {value ? (
        <div className="space-y-3">
          <div className="relative group">
            {/* Show video or image preview */}
            {isVideo ? (
              <div className="relative">
                <video 
                  src={value} 
                  className="w-full h-24 object-cover rounded-lg border border-border pointer-events-none" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline 
                />
                {/* Video badge */}
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-purple-500 text-white text-[10px] font-medium flex items-center gap-1">
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  Video
                </div>
              </div>
            ) : (
              <img src={value} alt="Banner" className="w-full h-24 object-cover rounded-lg border border-border" />
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white" disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              </button>
              <button type="button" onClick={() => onChange('')} className="p-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg text-white"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          {/* Dimension feedback - only for images */}
          {imageDimensions && !isVideo && (
            <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
              dimensionStatus === 'perfect' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
              dimensionStatus === 'good' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
              'bg-orange-500/10 text-orange-400 border border-orange-500/20'
            }`}>
              <div className="flex items-center gap-1.5">
                {dimensionStatus === 'perfect' ? (
                  <Check className="w-3.5 h-3.5 flex-shrink-0" />
                ) : dimensionStatus === 'good' ? (
                  <Check className="w-3.5 h-3.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                )}
                <span className="font-medium">
                  {imageDimensions.width}×{imageDimensions.height}px
                </span>
              </div>
              <span className="text-muted-foreground/80">—</span>
              {recommendedSize && (
                <span className={dimensionStatus === 'perfect' ? 'text-green-400/80' : 'text-muted-foreground'}>
                  {dimensionStatus === 'perfect' ? 'Perfect match!' :
                   dimensionStatus === 'good' ? 'Aspect ratio OK, will be scaled' :
                   `Recommended: ${recommendedSize.width}×${recommendedSize.height}px`}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div onClick={() => !uploading && fileInputRef.current?.click()}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          className={`h-28 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${dragOver ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {uploading ? (<><Loader2 className="w-5 h-5 animate-spin text-primary mb-1" /><span className="text-xs text-muted-foreground">Uploading...</span></>) 
            : (<>
              <Upload className="w-5 h-5 text-muted-foreground mb-1" />
              <span className="text-xs text-muted-foreground">Click or drag image/video</span>
              {recommendedSize && <span className="text-xs text-muted-foreground/70 mt-0.5">{recommendedSize.width}×{recommendedSize.height}px</span>}
              <span className="text-[10px] text-purple-400 mt-1 flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                MP4/WebM supported (max 15MB)
              </span>
            </>)}
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={(e) => handleUpload(e.target.files[0])} className="hidden" />
    </div>
  );
});

// Actual Size Modal
const PlacementSizeModal = memo(({ placement, onClose }) => {
  const [viewMode, setViewMode] = useState('desktop');
  const config = PLACEMENTS[placement];
  if (!config) return null;
  const dims = viewMode === 'mobile' ? config.mobile : config.desktop;
  const maxWidth = Math.min(window.innerWidth - 100, dims.width);
  const scale = maxWidth / dims.width;
  
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
      <div className="text-center" onClick={e => e.stopPropagation()}>
        <div className="mb-6 flex items-center justify-center gap-4">
          <h3 className="text-xl font-bold text-white">{config.name}</h3>
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
            <button onClick={() => setViewMode('desktop')} className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-primary text-white' : 'text-white/60'}`}><Monitor className="w-5 h-5" /></button>
            <button onClick={() => setViewMode('mobile')} className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-primary text-white' : 'text-white/60'}`}><Smartphone className="w-5 h-5" /></button>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-white/60"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative mx-auto rounded-xl overflow-hidden border-2 border-dashed" style={{ width: dims.width * scale, height: dims.height * scale, borderColor: config.color, backgroundColor: 'rgba(255,255,255,0.05)' }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">{dims.label}</div>
              <div className="text-sm text-white/60">{viewMode === 'desktop' ? 'Desktop' : 'Mobile'} • {config.type}</div>
            </div>
          </div>
        </div>
        <p className="text-white/50 text-sm mt-4">Click anywhere to close</p>
      </div>
    </div>
  );
});

// Live Preview Component - Uses aspect-ratio CSS to match frontend exactly
const LivePreview = memo(({ banner, placement, viewMode, isLarge = false, placementSettings }) => {
  const config = PLACEMENTS[placement];
  if (!config) return null;
  
  // Special handling for grid (in-grid) layout preview
  if (config.type === 'grid' && isLarge) {
    const interval = banner.listing_interval || 8;
    return (
      <div className="w-full">
        <div className="grid grid-cols-4 gap-2">
          {/* Simulated listing grid with ad */}
          {[...Array(12)].map((_, idx) => {
            const isAdSlot = (idx + 1) === interval;
            if (isAdSlot) {
              return (
                <div key={idx} className="relative rounded-lg overflow-hidden border-2 border-pink-500 bg-card" style={{ aspectRatio: '4/3' }}>
                  {banner.image_url ? (
                    <MediaPreview src={banner.image_url} alt="Ad" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-pink-500/20">
                      <Grid3X3 className="w-4 h-4 text-pink-500" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/30" />
                  <span className="absolute top-1 left-1 px-1 py-0.5 bg-pink-500 text-white text-[8px] rounded">Ad</span>
                  {banner.title && (
                    <div className="absolute bottom-1 left-1 right-1 text-white text-[8px] font-medium truncate drop-shadow">
                      {banner.title}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={idx} className="rounded-lg bg-secondary/50 border border-border" style={{ aspectRatio: '4/3' }}>
                <div className="w-full h-full flex items-center justify-center text-muted-foreground/30">
                  <LayoutGrid className="w-3 h-3" />
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-3 text-xs text-white/60">
          Ad appears every <span className="text-pink-400 font-medium">{interval}</span> listings
        </div>
      </div>
    );
  }
  
  // Calculate aspect ratio from config dimensions
  const getAspectRatio = () => {
    const dims = viewMode === 'mobile' ? config.mobile : config.desktop;
    return `${dims.width}/${dims.height}`;
  };
  
  // Special handling for double (2-up) layout
  if (config.type === 'double' && isLarge) {
    const isLeft = banner.double_position !== 'right';
    const aspectRatio = getAspectRatio();
    
    return (
      <div className="w-full">
        <div className={`grid ${viewMode === 'mobile' ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-4'}`}>
          {/* Left slot */}
          <div className={`relative rounded-lg overflow-hidden border-2 ${isLeft ? 'border-green-500' : 'border-dashed border-border'}`} 
               style={{ aspectRatio }}>
            {isLeft ? (
              <>
                {banner.image_url ? (
                  <MediaPreview src={banner.image_url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary"><Image className="w-6 h-6 text-muted-foreground" /></div>
                )}
                {(banner.overlay_opacity ?? 0) > 0 && (
                  <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${(banner.overlay_opacity) / 100})` }} />
                )}
                {banner.display_text && (
                  <div className="absolute bottom-2 left-2 text-white font-bold text-sm drop-shadow-lg">{banner.display_text}</div>
                )}
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-green-500 text-white text-[10px] rounded">Left</div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                <span>Left slot</span>
              </div>
            )}
          </div>
          
          {/* Right slot */}
          <div className={`relative rounded-lg overflow-hidden border-2 ${!isLeft ? 'border-blue-500' : 'border-dashed border-border'}`}
               style={{ aspectRatio }}>
            {!isLeft ? (
              <>
                {banner.image_url ? (
                  <MediaPreview src={banner.image_url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary"><Image className="w-6 h-6 text-muted-foreground" /></div>
                )}
                {(banner.overlay_opacity ?? 0) > 0 && (
                  <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${(banner.overlay_opacity) / 100})` }} />
                )}
                {banner.display_text && (
                  <div className="absolute bottom-2 left-2 text-white font-bold text-sm drop-shadow-lg">{banner.display_text}</div>
                )}
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded">Right</div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                <span>Right slot</span>
              </div>
            )}
          </div>
        </div>
        <div className="text-center mt-2 text-xs text-white/60">
          This banner will appear in the <span className={isLeft ? 'text-green-400' : 'text-blue-400'}>{isLeft ? 'Left' : 'Right'}</span> carousel
        </div>
      </div>
    );
  }
  
  // Standard preview using aspect-ratio CSS (matches frontend exactly)
  const aspectRatio = getAspectRatio();
  const textPos = banner.text_position || 'bottom-left';
  const buttonPos = banner.button_position || 'bottom-right';
  const textSize = banner.text_size || 'medium';
  const buttonStyle = banner.button_style || 'filled';
  const buttonColor = banner.button_color || '#3B82F6';
  const overlayOpacity = (banner.overlay_opacity ?? 0) / 100;
  
  const settings = placementSettings?.[placement] || { show_text: true, show_button: true };
  const showText = settings.show_text !== false;
  const showButton = settings.show_button !== false;
  
  // Dynamic font sizes based on preview size
  const titleFontSize = isLarge ? (viewMode === 'mobile' ? 14 : 18) : 10;
  const buttonFontSize = isLarge ? (viewMode === 'mobile' ? 10 : 12) : 8;
  
  const buttonStyles = buttonStyle === 'outline' 
    ? { backgroundColor: 'transparent', border: `2px solid ${buttonColor}`, color: buttonColor }
    : { backgroundColor: buttonColor, color: buttonColor === '#FFFFFF' || buttonColor === '#F59E0B' ? '#000000' : '#FFFFFF' };
  
  // Container width based on preview size
  const containerWidth = isLarge ? (viewMode === 'mobile' ? '280px' : '100%') : '120px';
  
  return (
    <div className="relative mx-auto" style={{ width: containerWidth }}>
      <div className="relative w-full rounded-lg overflow-hidden border border-border bg-secondary" 
           style={{ aspectRatio, boxShadow: isLarge ? '0 4px 20px rgba(0,0,0,0.3)' : 'none' }}>
        {banner.image_url ? (
          <MediaPreview src={banner.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900">
            <Image className={`${isLarge ? 'w-8 h-8' : 'w-4 h-4'} text-white/20`} />
          </div>
        )}
        {overlayOpacity > 0 && (
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} />
        )}
        {showText && (banner.display_text || banner.description) && (
          <div className="absolute max-w-[85%]" style={getPositionStyles(textPos)}>
            {banner.display_text && (
              <h3 className="text-white font-bold leading-tight drop-shadow-lg" style={{ fontSize: titleFontSize }}>{banner.display_text}</h3>
            )}
            {banner.description && isLarge && (
              <p className="text-white/80 text-xs mt-1 drop-shadow">{banner.description}</p>
            )}
          </div>
        )}
        {showButton && banner.button_text && (
          <div className="absolute" style={getPositionStyles(buttonPos)}>
            <span className="inline-block px-2 py-1 rounded font-medium shadow-lg whitespace-nowrap" style={{ fontSize: buttonFontSize, ...buttonStyles }}>{banner.button_text}</span>
          </div>
        )}
      </div>
    </div>
  );
});

// Compact Placement Card with visibility toggles
const PlacementCard = memo(({ placement, banner, viewMode, isSelected, onToggle, onPreview, onViewSize, placementSettings, onSettingsChange }) => {
  const config = PLACEMENTS[placement];
  const settings = placementSettings?.[placement] || { show_text: true, show_button: true };
  
  const toggleText = (e) => { e.stopPropagation(); onSettingsChange(placement, { ...settings, show_text: !settings.show_text }); };
  const toggleButton = (e) => { e.stopPropagation(); onSettingsChange(placement, { ...settings, show_button: !settings.show_button }); };
  const handleViewSize = (e) => { e.stopPropagation(); onViewSize(); };
  const handlePreview = (e) => { e.stopPropagation(); onPreview(); };
  
  return (
    <div 
      onClick={onToggle}
      className={`relative p-1.5 rounded-lg border-2 transition-all cursor-pointer ${isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 bg-card'}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground group-hover:border-primary'}`}>
          {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
        </div>
        {isSelected && (
          <div className="flex items-center gap-0.5">
            <button onClick={toggleText} className={`p-0.5 rounded transition-colors ${settings.show_text ? 'bg-blue-500/20 text-blue-400' : 'bg-secondary text-muted-foreground/50'}`} title={settings.show_text ? 'Hide text' : 'Show text'}><Type className="w-3 h-3" /></button>
            <button onClick={toggleButton} className={`p-0.5 rounded transition-colors ${settings.show_button ? 'bg-green-500/20 text-green-400' : 'bg-secondary text-muted-foreground/50'}`} title={settings.show_button ? 'Hide button' : 'Show button'}><Square className="w-3 h-3" /></button>
          </div>
        )}
        <button onClick={handleViewSize} className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground flex-shrink-0" title="View actual size"><Maximize2 className="w-3 h-3" /></button>
      </div>
      <div className="mb-1 flex justify-center" onClick={handlePreview} title="Click to preview">
        <LivePreview banner={banner} placement={placement} viewMode={viewMode} isLarge={false} placementSettings={placementSettings} />
      </div>
      <div className="flex items-center justify-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: config.color }} />
        <span className="text-[10px] font-medium truncate">{config.name}</span>
      </div>
      <div className="text-[8px] text-center text-muted-foreground mt-0.5">{config.type}</div>
    </div>
  );
});

// Banner Editor Modal - Professional version with device-specific settings
const BannerEditor = memo(({ banner, islands, categories, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: banner?.title || '',  // Internal name for admin reference
    display_text: banner?.display_text || '',  // Text shown ON the banner (optional)
    description: banner?.description || '',  // Subtitle shown on banner (optional)
    // Separate images for desktop and mobile
    image_url: banner?.image_url || '',
    mobile_image_url: banner?.mobile_image_url || '',
    poster_url: banner?.poster_url || '',  // Fallback image for video banners
    // Toggle for using same media: true if no mobile URL, or if mobile URL equals desktop URL
    use_single_image: !banner?.mobile_image_url || banner?.mobile_image_url === banner?.image_url,
    link_url: banner?.link_url || '',
    button_text: banner?.button_text || '',
    // Device-specific positioning
    desktop_text_position: banner?.desktop_text_position || banner?.text_position || 'bottom-left',
    desktop_button_position: banner?.desktop_button_position || banner?.button_position || 'bottom-right',
    mobile_text_position: banner?.mobile_text_position || 'bottom-center',
    mobile_button_position: banner?.mobile_button_position || 'bottom-center',
    // Legacy fields for backward compatibility
    text_position: banner?.text_position || 'bottom-left',
    button_position: banner?.button_position || 'bottom-right',
    text_size: banner?.text_size || 'medium',
    button_style: banner?.button_style || 'filled',
    button_color: banner?.button_color || '#3B82F6',
    overlay_opacity: banner?.overlay_opacity != null ? (banner.overlay_opacity > 1 ? banner.overlay_opacity : banner.overlay_opacity * 100) : 0,
    positions: banner?.positions || [],
    placement_settings: banner?.placement_settings || {},
    double_position: banner?.double_position || 'left',
    listing_interval: banner?.listing_interval || 8,
    target_islands: banner?.target_islands || [],
    target_categories: banner?.target_categories || [],
    company_name: banner?.company_name || '',
    priority: banner?.priority || 0,
    active: banner?.active ?? true
  });
  
  const [activeDevice, setActiveDevice] = useState('desktop'); // Which device settings are being edited
  const [previewPlacement, setPreviewPlacement] = useState(banner?.positions?.[0] || 'home_top');
  const [saving, setSaving] = useState(false);
  const [viewSizePlacement, setViewSizePlacement] = useState(null);

  // Get current placement config
  const currentPlacement = PLACEMENTS[previewPlacement];

  const handleSave = async () => {
    if (!formData.title) { toast.error('Title is required'); return; }
    if (!formData.image_url) { toast.error('Please upload a desktop image/video'); return; }
    if (!formData.use_single_image && !formData.mobile_image_url) { 
      toast.error('Please upload a mobile media or enable "Use same media"'); 
      return; 
    }
    
    // Sync legacy position fields with device-specific ones
    const saveData = {
      ...formData,
      text_position: formData.desktop_text_position,
      button_position: formData.desktop_button_position,
      // If using single image/video, copy desktop URL to mobile
      mobile_image_url: formData.use_single_image ? formData.image_url : formData.mobile_image_url,
    };
    
    // Remove the UI-only field before saving
    delete saveData.use_single_image;
    
    setSaving(true);
    try { await onSave(saveData); } finally { setSaving(false); }
  };

  const togglePlacement = (id) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.positions.includes(id);
      const newPositions = isCurrentlySelected ? prev.positions.filter(p => p !== id) : [...prev.positions, id];
      const newSettings = { ...prev.placement_settings };
      if (!isCurrentlySelected && !newSettings[id]) { newSettings[id] = { show_text: true, show_button: true }; }
      return { ...prev, positions: newPositions, placement_settings: newSettings };
    });
  };

  const updatePlacementSettings = (placementId, settings) => {
    setFormData(prev => ({ ...prev, placement_settings: { ...prev.placement_settings, [placementId]: settings } }));
  };

  const toggleIsland = (code) => {
    setFormData(prev => ({ ...prev, target_islands: prev.target_islands.includes(code) ? prev.target_islands.filter(i => i !== code) : [...prev.target_islands, code] }));
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({ ...prev, target_categories: prev.target_categories.includes(categoryId) ? prev.target_categories.filter(c => c !== categoryId) : [...prev.target_categories, categoryId] }));
  };

  const hasGridPlacement = formData.positions.includes('listing_grid');
  const hasCategoryLandingPlacement = formData.positions.includes('category_landing');
  const hasListingDetailPlacement = formData.positions.includes('listing_detail');
  const hasCategoryTargeting = hasGridPlacement || hasCategoryLandingPlacement || hasListingDetailPlacement;

  // Get current position based on device
  const getCurrentTextPosition = () => activeDevice === 'mobile' ? formData.mobile_text_position : formData.desktop_text_position;
  const getCurrentButtonPosition = () => activeDevice === 'mobile' ? formData.mobile_button_position : formData.desktop_button_position;
  
  const setTextPosition = (pos) => {
    if (activeDevice === 'mobile') {
      setFormData({ ...formData, mobile_text_position: pos });
    } else {
      setFormData({ ...formData, desktop_text_position: pos, text_position: pos });
    }
  };
  
  const setButtonPosition = (pos) => {
    if (activeDevice === 'mobile') {
      setFormData({ ...formData, mobile_button_position: pos });
    } else {
      setFormData({ ...formData, desktop_button_position: pos, button_position: pos });
    }
  };

  // Get image URL based on device
  const getImageForDevice = (device) => {
    if (device === 'mobile' && !formData.use_single_image && formData.mobile_image_url) {
      return formData.mobile_image_url;
    }
    return formData.image_url;
  };

  // Create banner data for preview with device-specific settings
  const getPreviewBanner = (device) => ({
    ...formData,
    image_url: getImageForDevice(device),
    text_position: device === 'mobile' ? formData.mobile_text_position : formData.desktop_text_position,
    button_position: device === 'mobile' ? formData.mobile_button_position : formData.desktop_button_position,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen p-4 flex items-start justify-center">
        <div className="bg-card border border-border rounded-2xl w-full max-w-7xl my-4 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-secondary/50">
            <div>
              <h2 className="text-xl font-bold">{banner ? 'Edit Banner' : 'Create New Banner'}</h2>
              <p className="text-sm text-muted-foreground">Design responsive banners with device-specific settings</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid lg:grid-cols-12 min-h-[650px]">
            {/* Left Panel: Form Controls */}
            <div className="lg:col-span-4 border-r border-border overflow-y-auto max-h-[calc(100vh-200px)]">
              {/* Device Tabs */}
              <div className="sticky top-0 bg-card border-b border-border p-3 z-10">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveDevice('desktop')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      activeDevice === 'desktop' 
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    <Monitor className="w-4 h-4" />
                    Desktop
                  </button>
                  <button
                    onClick={() => setActiveDevice('mobile')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      activeDevice === 'mobile' 
                        ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    <Smartphone className="w-4 h-4" />
                    Mobile
                  </button>
                </div>
                <div className={`mt-2 px-3 py-1.5 rounded-lg text-xs text-center ${
                  activeDevice === 'desktop' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'
                }`}>
                  {activeDevice === 'desktop' 
                    ? `Desktop: ${currentPlacement?.desktop.label || '1200×400 (3:1)'}` 
                    : `Mobile: ${currentPlacement?.mobile.label || '800×450 (16:9)'}`}
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Image Upload Section */}
                <div className={`p-4 rounded-xl border-2 ${
                  activeDevice === 'desktop' ? 'border-blue-500/30 bg-blue-500/5' : 'border-purple-500/30 bg-purple-500/5'
                }`}>
                  <label className="flex items-center gap-2 text-sm font-semibold mb-3">
                    {activeDevice === 'desktop' ? <Monitor className="w-4 h-4 text-blue-500" /> : <Smartphone className="w-4 h-4 text-purple-500" />}
                    {activeDevice === 'desktop' ? 'Desktop Image' : 'Mobile Image'} *
                  </label>
                  
                  {activeDevice === 'desktop' ? (
                    <ImageUploader 
                      value={formData.image_url} 
                      onChange={(url) => setFormData({ ...formData, image_url: url })}
                      recommendedSize={currentPlacement?.desktop}
                    />
                  ) : (
                    <>
                      {/* Mobile Media Choice - Prominent Toggle */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, use_single_image: true })}
                          className={`p-3 rounded-xl border-2 text-center transition-all ${
                            formData.use_single_image 
                              ? 'border-green-500 bg-green-500/10 text-green-400' 
                              : 'border-border bg-secondary hover:border-green-500/50'
                          }`}
                        >
                          <Monitor className="w-5 h-5 mx-auto mb-1" />
                          <div className="text-xs font-medium">Use Desktop Media</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">Same file for both devices</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, use_single_image: false })}
                          className={`p-3 rounded-xl border-2 text-center transition-all ${
                            !formData.use_single_image 
                              ? 'border-purple-500 bg-purple-500/10 text-purple-400' 
                              : 'border-border bg-secondary hover:border-purple-500/50'
                          }`}
                        >
                          <Upload className="w-5 h-5 mx-auto mb-1" />
                          <div className="text-xs font-medium">Upload Separate</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">Optimized for mobile</div>
                        </button>
                      </div>
                      
                      {formData.use_single_image ? (
                        <div className="p-4 border-2 border-dashed border-green-500/30 rounded-xl bg-green-500/5 text-center">
                          <div className="flex items-center justify-center gap-2 text-green-400">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">Using Desktop Media</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            The desktop image/video will be used on mobile devices
                          </p>
                        </div>
                      ) : (
                        <ImageUploader 
                          value={formData.mobile_image_url} 
                          onChange={(url) => setFormData({ ...formData, mobile_image_url: url })}
                          recommendedSize={currentPlacement?.mobile}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Poster Image (Fallback) - shown when video is uploaded */}
                {isVideoUrl(formData.image_url) && (
                  <div className="p-3 border border-purple-500/30 rounded-lg bg-purple-500/5">
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                      Poster Image (Video Fallback)
                      <span className="text-xs text-muted-foreground font-normal ml-auto">Optional</span>
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Shown when video can't autoplay (slow connection, data saver mode, reduced motion preference)
                    </p>
                    <ImageUploader 
                      value={formData.poster_url} 
                      onChange={(url) => setFormData({ ...formData, poster_url: url })}
                      recommendedSize={currentPlacement?.desktop}
                    />
                  </div>
                )}

                {/* Content Fields */}
                <div className="space-y-3">
                  {/* Internal Title - for admin reference */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Banner Name * <span className="text-xs text-muted-foreground font-normal">(internal reference)</span>
                    </label>
                    <input 
                      type="text" 
                      value={formData.title} 
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                      placeholder="e.g. Summer Sale Campaign 2024" 
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary" 
                    />
                  </div>
                  
                  {/* Display Text - shown ON the banner */}
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl space-y-3">
                    <label className="block text-sm font-semibold text-primary">
                      Banner Content <span className="text-xs font-normal text-muted-foreground">(displayed on banner)</span>
                    </label>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Headline</label>
                      <input 
                        type="text" 
                        value={formData.display_text} 
                        onChange={(e) => setFormData({ ...formData, display_text: e.target.value })} 
                        placeholder="e.g. 50% Off All Items!" 
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Subtitle</label>
                      <input 
                        type="text" 
                        value={formData.description} 
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                        placeholder="e.g. Limited time offer - Shop now!" 
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" 
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Leave empty for image-only banners (no text overlay)
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Button Text</label>
                      <input 
                        type="text" 
                        value={formData.button_text} 
                        onChange={(e) => setFormData({ ...formData, button_text: e.target.value })} 
                        placeholder="Shop Now" 
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Link URL</label>
                      <input 
                        type="url" 
                        value={formData.link_url} 
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })} 
                        placeholder="https://..." 
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm" 
                      />
                    </div>
                  </div>
                </div>

                {/* Device-Specific Positioning */}
                <div className={`p-4 rounded-xl border ${
                  activeDevice === 'desktop' ? 'border-blue-500/30 bg-blue-500/5' : 'border-purple-500/30 bg-purple-500/5'
                }`}>
                  <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <GripVertical className="w-4 h-4" />
                    {activeDevice === 'desktop' ? 'Desktop' : 'Mobile'} Positioning
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <PositionSelector 
                      label="Text Position" 
                      value={getCurrentTextPosition()} 
                      onChange={setTextPosition} 
                      color={activeDevice === 'desktop' ? '#3B82F6' : '#A855F7'} 
                    />
                    <PositionSelector 
                      label="Button Position" 
                      value={getCurrentButtonPosition()} 
                      onChange={setButtonPosition} 
                      color="#10B981" 
                    />
                  </div>
                </div>

                {/* Styling (shared across devices) */}
                <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Palette className="w-4 h-4" /> Styling
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Text Size</label>
                    <div className="flex gap-1">
                      {TEXT_SIZES.map(s => (
                        <button 
                          key={s.id} 
                          type="button" 
                          onClick={() => setFormData({ ...formData, text_size: s.id })} 
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                            formData.text_size === s.id ? 'bg-primary text-primary-foreground' : 'bg-secondary border border-border'
                          }`} 
                          title={s.title}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Button Style</label>
                    <div className="flex gap-1">
                      {BUTTON_STYLES.map(s => (
                        <button 
                          key={s.id} 
                          type="button" 
                          onClick={() => setFormData({ ...formData, button_style: s.id })} 
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                            formData.button_style === s.id ? 'bg-primary text-primary-foreground' : 'bg-secondary border border-border'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Button Color</label>
                    <div className="flex flex-wrap gap-1.5">
                      {BUTTON_COLORS.map(c => (
                        <button 
                          key={c.id} 
                          type="button" 
                          onClick={() => setFormData({ ...formData, button_color: c.id })} 
                          className={`w-7 h-7 rounded-full border-2 transition-all ${
                            formData.button_color === c.id ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110' : 'border-border'
                          }`} 
                          style={{ backgroundColor: c.id }} 
                          title={c.label} 
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                      <Sun className="w-3.5 h-3.5" /> Overlay: {formData.overlay_opacity}%
                    </label>
                    <input 
                      type="range" 
                      min="0" 
                      max="80" 
                      value={formData.overlay_opacity} 
                      onChange={(e) => setFormData({ ...formData, overlay_opacity: parseInt(e.target.value) })} 
                      className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary" 
                    />
                  </div>
                </div>

                {/* Additional Settings */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Advertiser</label>
                      <input 
                        type="text" 
                        value={formData.company_name} 
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} 
                        placeholder="Company name" 
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Priority</label>
                      <input 
                        type="number" 
                        value={formData.priority} 
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })} 
                        min="0" 
                        max="100" 
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm" 
                      />
                    </div>
                  </div>

                  {/* Target Islands */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Target Islands</label>
                    <div className="flex flex-wrap gap-1.5">
                      {islands.map(island => (
                        <button 
                          key={island.code} 
                          type="button" 
                          onClick={() => toggleIsland(island.code)} 
                          className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                            formData.target_islands.includes(island.code) ? 'bg-primary text-primary-foreground' : 'bg-secondary border border-border'
                          }`}
                        >
                          {island.flag} {island.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 2-Up Position for home_middle and home_bottom */}
                  {(formData.positions.includes('home_middle') || formData.positions.includes('home_bottom')) && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                      <label className="block text-sm font-medium mb-2 text-amber-500">2-Up Position</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, double_position: 'left' })}
                          className={`p-2.5 rounded-lg border text-center text-sm transition-all ${
                            formData.double_position !== 'right' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-secondary border-border'
                          }`}
                        >
                          Left Carousel
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, double_position: 'right' })}
                          className={`p-2.5 rounded-lg border text-center text-sm transition-all ${
                            formData.double_position === 'right' ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'bg-secondary border-border'
                          }`}
                        >
                          Right Carousel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* In-Grid Settings */}
                  {hasGridPlacement && (
                    <div className="p-3 bg-pink-500/10 border border-pink-500/30 rounded-xl space-y-2">
                      <div className="flex items-center gap-2">
                        <Grid3X3 className="w-4 h-4 text-pink-500" />
                        <label className="text-sm font-medium text-pink-500">In-Grid Settings</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Show every</span>
                        <input
                          type="number"
                          min="4"
                          max="50"
                          value={formData.listing_interval}
                          onChange={(e) => setFormData({ ...formData, listing_interval: parseInt(e.target.value) || 8 })}
                          className="w-16 px-2 py-1.5 bg-secondary border border-border rounded text-sm text-center"
                        />
                        <span className="text-xs text-muted-foreground">listings</span>
                      </div>
                    </div>
                  )}

                  {/* Category Targeting - Shows when category_landing or listing_grid is selected */}
                  {hasCategoryTargeting && categories.length > 0 && (
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-emerald-500" />
                          <label className="text-sm font-medium text-emerald-500">Target Categories</label>
                        </div>
                        {formData.target_categories.length > 0 && (
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                            {formData.target_categories.length} selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formData.target_categories.length === 0 
                          ? 'No categories selected = shows on ALL category pages' 
                          : 'Ad will only show on selected category pages'}
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
                        {categories.map(category => {
                          const isSelected = formData.target_categories.includes(category.id);
                          return (
                            <button 
                              key={category.id}
                              type="button" 
                              onClick={() => toggleCategory(category.id)} 
                              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all ${
                                isSelected 
                                  ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-400' 
                                  : 'bg-secondary/50 border border-transparent hover:border-emerald-500/30'
                              }`}
                            >
                              <span className="font-medium">{category.name}</span>
                              {isSelected && <Check className="w-4 h-4" />}
                            </button>
                          );
                        })}
                      </div>
                      {formData.target_categories.length > 0 && (
                        <button 
                          type="button" 
                          onClick={() => setFormData({ ...formData, target_categories: [] })}
                          className="w-full text-xs text-muted-foreground hover:text-foreground py-1.5 border border-dashed border-border rounded-lg hover:border-muted-foreground transition-colors"
                        >
                          Clear all selections (show on all categories)
                        </button>
                      )}
                    </div>
                  )}

                  {/* Active Toggle */}
                  <div className="flex items-center justify-between p-3 bg-secondary rounded-xl">
                    <div>
                      <div className="font-medium">Active</div>
                      <div className="text-xs text-muted-foreground">{formData.active ? 'Banner is live' : 'Banner is hidden'}</div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setFormData({ ...formData, active: !formData.active })} 
                      className={`relative w-12 h-6 rounded-full transition-colors ${formData.active ? 'bg-green-500' : 'bg-secondary border border-border'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.active ? 'translate-x-7' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Center Panel: Side-by-Side Preview */}
            <div className="lg:col-span-5 p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">Live Preview</h3>
                <span className="px-3 py-1 rounded-full text-xs text-white font-medium" style={{ backgroundColor: currentPlacement?.color }}>
                  {currentPlacement?.name}
                </span>
              </div>
              
              {/* Desktop Preview */}
              <div className="mb-4">
                <div className={`flex items-center gap-2 mb-2 ${activeDevice === 'desktop' ? 'text-blue-400' : 'text-white/60'}`}>
                  <Monitor className="w-4 h-4" />
                  <span className="text-sm font-medium">Desktop Preview</span>
                  <span className="text-xs opacity-60">{currentPlacement?.desktop.label}</span>
                </div>
                <div className={`rounded-xl overflow-hidden border-2 transition-all ${
                  activeDevice === 'desktop' ? 'border-blue-500 shadow-lg shadow-blue-500/20' : 'border-white/10'
                }`}>
                  <LivePreview 
                    banner={getPreviewBanner('desktop')} 
                    placement={previewPlacement} 
                    viewMode="desktop" 
                    isLarge={true} 
                    placementSettings={formData.placement_settings} 
                  />
                </div>
              </div>
              
              {/* Mobile Preview */}
              <div className="flex-1">
                <div className={`flex items-center gap-2 mb-2 ${activeDevice === 'mobile' ? 'text-purple-400' : 'text-white/60'}`}>
                  <Smartphone className="w-4 h-4" />
                  <span className="text-sm font-medium">Mobile Preview</span>
                  <span className="text-xs opacity-60">{currentPlacement?.mobile.label}</span>
                </div>
                <div className="flex justify-center">
                  <div className={`w-[280px] rounded-xl overflow-hidden border-2 transition-all ${
                    activeDevice === 'mobile' ? 'border-purple-500 shadow-lg shadow-purple-500/20' : 'border-white/10'
                  }`}>
                    <LivePreview 
                      banner={getPreviewBanner('mobile')} 
                      placement={previewPlacement} 
                      viewMode="mobile" 
                      isLarge={true} 
                      placementSettings={formData.placement_settings} 
                    />
                  </div>
                </div>
              </div>

              {/* Status indicators */}
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-white/50">
                <span className="flex items-center gap-1"><Type className="w-3 h-3" /> {formData.text_size}</span>
                <span className="flex items-center gap-1"><Square className="w-3 h-3" /> {formData.button_style}</span>
                <span className="flex items-center gap-1"><Sun className="w-3 h-3" /> {formData.overlay_opacity}%</span>
              </div>
            </div>

            {/* Right Panel: Placements */}
            <div className="lg:col-span-3 p-3 bg-secondary/30 border-l border-border overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">Placements</h3>
                <span className="text-xs text-muted-foreground">{formData.positions.length} selected</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Select where this banner will appear</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.values(PLACEMENTS).map(p => (
                  <PlacementCard 
                    key={p.id} 
                    placement={p.id} 
                    banner={formData} 
                    viewMode={activeDevice} 
                    isSelected={formData.positions.includes(p.id)} 
                    onToggle={() => togglePlacement(p.id)} 
                    onPreview={() => setPreviewPlacement(p.id)} 
                    onViewSize={() => setViewSizePlacement(p.id)} 
                    placementSettings={formData.placement_settings} 
                    onSettingsChange={updatePlacementSettings} 
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-border bg-secondary/50">
            <div className="text-sm text-muted-foreground">
              {formData.positions.length === 0 && <span className="text-amber-400">Select at least one placement</span>}
            </div>
            <div className="flex gap-3">
              <button 
                onClick={onClose} 
                className="px-5 py-2 border border-border rounded-xl hover:bg-secondary font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave} 
                disabled={saving || formData.positions.length === 0} 
                className="px-6 py-2 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {banner ? 'Save Changes' : 'Create Banner'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Size Preview Modal */}
      {viewSizePlacement && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setViewSizePlacement(null)}>
          <div className="bg-card rounded-xl p-4 max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">{PLACEMENTS[viewSizePlacement]?.name} - Actual Size Preview</h4>
              <button onClick={() => setViewSizePlacement(null)} className="p-1 hover:bg-secondary rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-auto">
              <LivePreview banner={formData} placement={viewSizePlacement} viewMode={activeDevice} isLarge={true} placementSettings={formData.placement_settings} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// Ad Stats Modal - Detailed performance analytics per ad
const AdStatsModal = memo(({ banner, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [exporting, setExporting] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`${API}/admin/ads/${banner.id}/stats?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch ad stats:', error);
      toast.error('Failed to load ad statistics');
    } finally {
      setLoading(false);
    }
  }, [banner.id, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await fetch(`${API}/admin/ads/${banner.id}/export?${params.toString()}`);
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ad_report_${banner.title?.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-secondary/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <BarChart2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Ad Performance</h2>
              <p className="text-sm text-muted-foreground">{banner.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Date Filter */}
        <div className="flex items-center gap-4 p-4 border-b border-border bg-secondary/10">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Date Range:</span>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-1.5 bg-secondary border border-border rounded-lg text-sm"
          />
          <span className="text-muted-foreground">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-1.5 bg-secondary border border-border rounded-lg text-sm"
          />
          <button
            onClick={fetchStats}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Apply
          </button>
          <div className="flex-1" />
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : stats ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-purple-400 mb-1">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs font-medium">Impressions</span>
                  </div>
                  <div className="text-2xl font-bold">{stats.totals?.impressions?.toLocaleString() || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-1">
                    <MousePointer className="w-4 h-4" />
                    <span className="text-xs font-medium">Clicks</span>
                  </div>
                  <div className="text-2xl font-bold">{stats.totals?.clicks?.toLocaleString() || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-400 mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-xs font-medium">CTR</span>
                  </div>
                  <div className="text-2xl font-bold">{stats.totals?.ctr || 0}%</div>
                </div>
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-amber-400 mb-1">
                    <Activity className="w-4 h-4" />
                    <span className="text-xs font-medium">Placements</span>
                  </div>
                  <div className="text-2xl font-bold">{stats.placement_breakdown?.length || 0}</div>
                </div>
              </div>

              {/* Placement Breakdown */}
              <div className="bg-secondary/30 border border-border rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-border bg-secondary/50">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Performance by Placement
                  </h3>
                </div>
                <div className="divide-y divide-border">
                  {stats.placement_breakdown?.length > 0 ? (
                    stats.placement_breakdown.map((placement) => (
                      <div key={placement.placement_id} className="flex items-center px-4 py-3">
                        <div className="flex items-center gap-3 flex-1">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: PLACEMENTS[placement.placement_id]?.color || '#666' }}
                          />
                          <span className="font-medium">{placement.placement_name}</span>
                        </div>
                        <div className="flex items-center gap-8 text-sm">
                          <div className="text-center">
                            <div className="text-muted-foreground text-xs">Impressions</div>
                            <div className="font-semibold text-purple-400">{placement.impressions.toLocaleString()}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-muted-foreground text-xs">Clicks</div>
                            <div className="font-semibold text-blue-400">{placement.clicks.toLocaleString()}</div>
                          </div>
                          <div className="text-center w-16">
                            <div className="text-muted-foreground text-xs">CTR</div>
                            <div className={`font-semibold ${placement.ctr >= 2 ? 'text-green-400' : 'text-amber-400'}`}>
                              {placement.ctr}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-8 text-center text-muted-foreground">
                      No data available for the selected period
                    </div>
                  )}
                </div>
              </div>

              {/* Daily Trend */}
              {stats.daily_data?.length > 0 && (
                <div className="bg-secondary/30 border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-secondary/50">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Daily Breakdown
                    </h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/50 sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Date</th>
                          <th className="text-right px-4 py-2 font-medium">Impressions</th>
                          <th className="text-right px-4 py-2 font-medium">Clicks</th>
                          <th className="text-right px-4 py-2 font-medium">CTR</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {stats.daily_data.map((day) => {
                          const dayCtr = day.impressions > 0 ? (day.clicks / day.impressions * 100).toFixed(2) : 0;
                          return (
                            <tr key={day.date} className="hover:bg-secondary/30">
                              <td className="px-4 py-2">{day.date}</td>
                              <td className="text-right px-4 py-2 text-purple-400">{day.impressions}</td>
                              <td className="text-right px-4 py-2 text-blue-400">{day.clicks}</td>
                              <td className={`text-right px-4 py-2 ${parseFloat(dayCtr) >= 2 ? 'text-green-400' : 'text-muted-foreground'}`}>
                                {dayCtr}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-20 text-muted-foreground">
              No statistics available
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Banner Grid Card
const BannerCard = memo(({ banner, onEdit, onDelete, onToggle, onViewStats }) => {
  const ctr = banner.impressions > 0 ? ((banner.clicks / banner.impressions) * 100).toFixed(1) : 0;
  const hasTargetCategories = banner.target_categories && banner.target_categories.length > 0;
  const hasCategoryPlacement = banner.positions?.some(p => p === 'category_landing' || p === 'listing_grid');
  
  return (
    <div className={`group bg-card border border-border rounded-xl overflow-hidden transition-all hover:border-primary/50 ${!banner.active ? 'opacity-60' : ''}`}>
      <div className="relative aspect-[2/1] bg-secondary">
        {banner.image_url ? (<MediaPreview src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />) : (<div className="flex items-center justify-center h-full"><Image className="w-8 h-8 text-muted-foreground/30" /></div>)}
        {/* Video indicator badge */}
        {isVideoUrl(banner.image_url) && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-purple-500 text-white text-xs font-medium flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            Video
          </div>
        )}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${banner.active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>{banner.active ? 'Active' : 'Inactive'}</div>
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button onClick={() => onViewStats(banner)} className="p-2 bg-green-500/80 hover:bg-green-500 rounded-lg text-white" title="View Stats"><BarChart2 className="w-5 h-5" /></button>
          <button onClick={() => onToggle(banner)} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white" title={banner.active ? 'Deactivate' : 'Activate'}>{banner.active ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
          <button onClick={() => onEdit(banner)} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white" title="Edit"><Edit2 className="w-5 h-5" /></button>
          <button onClick={() => onDelete(banner.id)} className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white" title="Delete"><Trash2 className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold truncate">{banner.title}</h3>
        {banner.company_name && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" /> {banner.company_name}</p>}
        <div className="flex items-center gap-4 mt-3 text-xs">
          <span className="flex items-center gap-1 text-purple-400"><Eye className="w-3.5 h-3.5" /> {(banner.impressions || 0).toLocaleString()}</span>
          <span className="flex items-center gap-1 text-blue-400"><MousePointer className="w-3.5 h-3.5" /> {banner.clicks || 0}</span>
          <span className={`flex items-center gap-1 ${parseFloat(ctr) >= 2 ? 'text-green-400' : 'text-amber-400'}`}><BarChart2 className="w-3.5 h-3.5" /> {ctr}%</span>
        </div>
        {banner.positions?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {banner.positions.slice(0, 3).map(pos => (<span key={pos} className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: PLACEMENTS[pos]?.color || '#666' }}>{PLACEMENTS[pos]?.name || pos}</span>))}
            {banner.positions.length > 3 && <span className="px-2 py-0.5 rounded text-xs bg-secondary">+{banner.positions.length - 3}</span>}
          </div>
        )}
        {/* Category Targeting Indicator */}
        {hasCategoryPlacement && (
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            <Tag className="w-3 h-3 text-emerald-500" />
            {hasTargetCategories ? (
              <span className="text-emerald-400">{banner.target_categories.length} categorie{banner.target_categories.length !== 1 ? 'ën' : ''}</span>
            ) : (
              <span className="text-muted-foreground">Alle categorieën</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// Stat Card
const StatCard = memo(({ label, value, colorClass }) => (<div className={`${colorClass} rounded-xl p-4`}><div className="text-2xl font-bold">{value}</div><div className="text-sm opacity-80">{label}</div></div>));

// Placement Settings Component - Handles both Carousel (max_ads) and Grid (interval) settings
const PlacementSettingsPanel = memo(() => {
  const [settings, setSettings] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/placement-settings`);
        setSettings(response.data || {});
      } catch (error) {
        console.error('Failed to fetch placement settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const updateSetting = async (placementId, value, settingType) => {
    setSaving(placementId);
    try {
      const params = new URLSearchParams();
      if (settingType === 'interval') {
        params.append('ad_interval', value);
      } else {
        params.append('max_ads', value);
      }
      
      await axios.put(`${API}/placement-settings/${placementId}?${params.toString()}`);
      
      setSettings(prev => ({
        ...prev,
        [placementId]: { 
          ...prev[placementId], 
          [settingType === 'interval' ? 'ad_interval' : 'max_ads']: value 
        }
      }));
      toast.success(`Updated ${PLACEMENTS[placementId]?.name || placementId}`);
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <div className="h-16 bg-secondary/50 rounded-xl animate-pulse" />;
  }

  // Separate carousel and grid placements
  const carouselPlacements = Object.values(PLACEMENTS).filter(p => p.type !== 'grid');
  const gridPlacements = Object.values(PLACEMENTS).filter(p => p.type === 'grid');

  return (
    <div className="space-y-4">
      {/* Carousel Settings */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Carousel Ad Limits</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Maximum number of ads to show in each carousel. Ads are sorted by priority (highest first).
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {carouselPlacements.map(placement => {
            const currentMax = settings[placement.id]?.max_ads || 5;
            const hasUnsavedChanges = pendingChanges[placement.id]?.max_ads !== undefined && 
                                       pendingChanges[placement.id]?.max_ads !== currentMax;
            return (
              <div key={placement.id} className={`p-3 rounded-lg transition-colors ${hasUnsavedChanges ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-secondary/50'}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: placement.color }} />
                  <span className="text-xs font-medium truncate">{placement.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={pendingChanges[placement.id]?.max_ads ?? currentMax}
                    onChange={(e) => {
                      const val = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                      setPendingChanges(prev => ({
                        ...prev,
                        [placement.id]: { ...prev[placement.id], max_ads: val, type: 'max' }
                      }));
                    }}
                    className="w-14 px-2 py-1 text-center bg-background border border-border rounded text-sm"
                    disabled={saving === placement.id}
                  />
                  <span className="text-xs text-muted-foreground">max</span>
                  {saving === placement.id && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                  {hasUnsavedChanges && saving !== placement.id && (
                    <button
                      onClick={() => {
                        const val = pendingChanges[placement.id]?.max_ads;
                        if (val !== undefined) {
                          updateSetting(placement.id, val, 'max');
                          setPendingChanges(prev => {
                            const newChanges = { ...prev };
                            delete newChanges[placement.id];
                            return newChanges;
                          });
                        }
                      }}
                      className="px-2 py-0.5 bg-primary text-white text-xs rounded hover:bg-primary/90 transition-colors"
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Grid Ad Settings */}
      {gridPlacements.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Grid3X3 className="w-5 h-5 text-pink-500" />
            <h3 className="font-semibold">In-Grid Ad Frequency</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            How often ads appear between marketplace listings. An interval of 8 means one ad appears every 8 listings.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gridPlacements.map(placement => {
              const currentInterval = settings[placement.id]?.ad_interval || 8;
              const hasUnsavedChanges = pendingChanges[placement.id]?.ad_interval !== undefined && 
                                         pendingChanges[placement.id]?.ad_interval !== currentInterval;
              return (
                <div key={placement.id} className={`p-3 rounded-lg transition-colors ${hasUnsavedChanges ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-secondary/50'}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: placement.color }} />
                    <span className="text-xs font-medium truncate">{placement.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">every</span>
                    <input
                      type="number"
                      min="4"
                      max="50"
                      value={pendingChanges[placement.id]?.ad_interval ?? currentInterval}
                      onChange={(e) => {
                        const val = Math.max(4, Math.min(50, parseInt(e.target.value) || 8));
                        setPendingChanges(prev => ({
                          ...prev,
                          [placement.id]: { ...prev[placement.id], ad_interval: val, type: 'interval' }
                        }));
                      }}
                      className="w-14 px-2 py-1 text-center bg-background border border-border rounded text-sm"
                      disabled={saving === placement.id}
                    />
                    <span className="text-xs text-muted-foreground">items</span>
                    {saving === placement.id && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                    {hasUnsavedChanges && saving !== placement.id && (
                      <button
                        onClick={() => {
                          const val = pendingChanges[placement.id]?.ad_interval;
                          if (val !== undefined) {
                            updateSetting(placement.id, val, 'interval');
                            setPendingChanges(prev => {
                              const newChanges = { ...prev };
                              delete newChanges[placement.id];
                              return newChanges;
                            });
                          }
                        }}
                        className="px-2 py-0.5 bg-primary text-white text-xs rounded hover:bg-primary/90 transition-colors"
                      >
                        Save
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

// Main Component
const AdminAdvertising = () => {
  const { user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [islands, setIslands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [statsModal, setStatsModal] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const headers = {};
      const [bannersRes, islandsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/banners/admin`, { headers }), 
        axios.get(`${API}/islands`, { headers }),
        axios.get(`${API}/categories`, { headers })
      ]);
      setBanners(bannersRes.data || []);
      setIslands(islandsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) { toast.error('Failed to load data'); } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      const config = { };
      if (editingBanner) { await axios.put(`${API}/banners/${editingBanner.id}`, formData, config); toast.success('Banner updated'); }
      else { await axios.post(`${API}/banners`, formData, config); toast.success('Banner created'); }
      setShowEditor(false); setEditingBanner(null); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try { await axios.delete(`${API}/banners/${id}`); toast.success('Deleted'); setBanners(prev => prev.filter(b => b.id !== id)); } catch (error) { toast.error('Failed to delete'); }
  };

  const handleToggle = async (banner) => {
    try { await axios.put(`${API}/banners/${banner.id}`, { active: !banner.active }); toast.success(banner.active ? 'Deactivated' : 'Activated'); setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, active: !b.active } : b)); } catch (error) { toast.error('Failed to update'); }
  };

  const filteredBanners = useMemo(() => banners.filter(b => { const matchesSearch = b.title?.toLowerCase().includes(searchQuery.toLowerCase()); const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' && b.active) || (filterStatus === 'inactive' && !b.active); return matchesSearch && matchesStatus; }), [banners, searchQuery, filterStatus]);

  const stats = useMemo(() => ({ total: banners.length, active: banners.filter(b => b.active).length, impressions: banners.reduce((s, b) => s + (b.impressions || 0), 0), clicks: banners.reduce((s, b) => s + (b.clicks || 0), 0) }), [banners]);

  if (loading) return (<div className="space-y-6"><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-secondary/50 rounded-xl animate-pulse" />)}</div><div className="flex gap-3"><div className="flex-1 h-10 bg-secondary/50 rounded-lg animate-pulse" /><div className="w-32 h-10 bg-secondary/50 rounded-lg animate-pulse" /></div><div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[1,2,3].map(i => <div key={i} className="h-64 bg-secondary/50 rounded-xl animate-pulse" />)}</div></div>);

  return (
    <div className="space-y-6" data-testid="admin-advertising">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={stats.total} colorClass="bg-purple-500/20 text-purple-400" />
        <StatCard label="Active" value={stats.active} colorClass="bg-green-500/20 text-green-400" />
        <StatCard label="Impressions" value={stats.impressions.toLocaleString()} colorClass="bg-blue-500/20 text-blue-400" />
        <StatCard label="Clicks" value={stats.clicks.toLocaleString()} colorClass="bg-amber-500/20 text-amber-400" />
      </div>
      
      {/* Placement Settings Panel */}
      <PlacementSettingsPanel />
      
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><input type="text" placeholder="Search banners..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-secondary border border-border rounded-lg" /></div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 bg-secondary border border-border rounded-lg"><option value="all">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
        <button onClick={fetchData} className="p-2 bg-secondary hover:bg-secondary/80 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
        <button onClick={() => { setEditingBanner(null); setShowEditor(true); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"><Plus className="w-4 h-4" /> Create Banner</button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredBanners.map(banner => (
          <BannerCard 
            key={banner.id} 
            banner={banner} 
            onEdit={(b) => { setEditingBanner(b); setShowEditor(true); }} 
            onDelete={handleDelete} 
            onToggle={handleToggle} 
            onViewStats={(b) => setStatsModal(b)}
          />
        ))}
        {filteredBanners.length === 0 && (<div className="col-span-full text-center py-12"><Megaphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" /><p className="text-muted-foreground">No banners found</p></div>)}
      </div>
      {showEditor && <BannerEditor banner={editingBanner} islands={islands} categories={categories} onSave={handleSave} onClose={() => { setShowEditor(false); setEditingBanner(null); }} />}
      {statsModal && <AdStatsModal banner={statsModal} onClose={() => setStatsModal(null)} />}
    </div>
  );
};

export default AdminAdvertising;
